import React, { useState, useEffect } from 'react';
import { Table, Tag, Typography, Space, Statistic, Card, Button, message } from 'antd';
import { StarOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import AdminLayout from '../Layout/AdminLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Text } = Typography;

const AREA_TAG_COLOR = { KV1: 'blue', 'KV2-NT': 'cyan', KV2: 'green', KV3: 'default' };

const IELTS_COLOR = (score) => {
    if (score >= 7.0) return '#52c41a';
    if (score >= 6.0) return '#1890ff';
    if (score >= 5.0) return '#faad14';
    return '#d4b106';
};

const buildColumns = (navigate) => [
    {
        title: 'Mã hồ sơ',
        dataIndex: 'student_code',
        width: 120,
        sorter: (a, b) => (a.student_code ?? '').localeCompare(b.student_code ?? ''),
        render: (v) => <Text code>{v}</Text>,
    },
    {
        title: 'Họ và tên',
        dataIndex: 'full_name',
        sorter: (a, b) => (a.full_name ?? '').localeCompare(b.full_name ?? ''),
    },
    {
        title: 'CCCD',
        dataIndex: 'identification',
        width: 150,
        render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
        title: 'Điểm IELTS',
        dataIndex: 'ielts_grade',
        width: 120,
        align: 'center',
        defaultSortOrder: 'descend',
        sorter: (a, b) => (a.ielts_grade ?? 0) - (b.ielts_grade ?? 0),
        render: (v) => (
            <Text strong style={{ color: IELTS_COLOR(v), fontSize: 15 }}>
                {v?.toFixed(1)}
            </Text>
        ),
    },
    {
        title: 'Khu vực',
        dataIndex: 'admission_area',
        width: 110,
        filters: [
            { text: 'KV1', value: 'KV1' },
            { text: 'KV2-NT', value: 'KV2-NT' },
            { text: 'KV2', value: 'KV2' },
            { text: 'KV3', value: 'KV3' },
        ],
        onFilter: (v, r) => r.admission_area === v,
        render: (v) => v
            ? <Tag color={AREA_TAG_COLOR[v] ?? 'default'}>{v}</Tag>
            : <Text type="secondary">—</Text>,
    },
    {
        title: 'Hành động',
        key: 'action',
        width: 100,
        align: 'center',
        render: (_, record) => (
            <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/student-profile/${record.id}`)}
            >
                Chi tiết
            </Button>
        ),
    },
];

const AdminIeltsList = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        axiosInstance.get('/api/v1/admin/students-with-ielts')
            .then((res) => {
                if (res.data?.success) setData(res.data.data ?? []);
            })
            .finally(() => setLoading(false));
    }, []);

    const exportToExcel = () => {
        try {
            setExporting(true);

            const excelData = data.map((s) => {
                const baseData = {
                    'Mã hồ sơ': s.student_code,
                    'Họ và tên': s.full_name,
                    'CCCD': s.identification,
                    'Điểm IELTS': s.ielts_grade,
                    'Khu vực': s.admission_area,
                };

                Object.values(s.attachments ?? {}).forEach((att) => {
                    baseData[`Tài liệu: ${att.type_name}`] = att.has_attachment ? att.file_name : '';
                });

                return baseData;
            });

            const ws = XLSX.utils.json_to_sheet(excelData);

            // json_to_sheet writes plain strings; turn attachment cells into
            // real HYPERLINK formulas so Excel renders them as clickable links.
            const fixedColumnCount = 5;
            const range = XLSX.utils.decode_range(ws['!ref']);
            for (let r = 0; r <= range.e.r - 1; r++) {
                const student = data[r];
                const attachments = Object.values(student.attachments ?? {});
                attachments.forEach((att, i) => {
                    if (!att.has_attachment) return;
                    const cellRef = XLSX.utils.encode_cell({ r: r + 1, c: fixedColumnCount + i });
                    ws[cellRef] = {
                        t: 'str',
                        f: `HYPERLINK("${att.download_url}","${att.file_name}")`,
                        v: att.file_name,
                    };
                });
            }

            ws['!cols'] = [
                { wch: 15 }, // Mã hồ sơ
                { wch: 25 }, // Họ và tên
                { wch: 15 }, // CCCD
                { wch: 12 }, // Điểm IELTS
                { wch: 12 }, // Khu vực
                { wch: 50 }, // Tài liệu: Chứng chỉ IELTS
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'DS điểm IELTS');

            const now = new Date();
            const formattedDate = now.toLocaleDateString('vi-VN', {
                year: 'numeric', month: '2-digit', day: '2-digit'
            }).replace(/\//g, '-');
            const formattedTime = now.toLocaleTimeString('vi-VN', {
                hour: '2-digit', minute: '2-digit', second: '2-digit'
            }).replace(/:/g, '-');

            XLSX.writeFile(wb, `danh-sach-diem-ielts-${formattedDate}_${formattedTime}.xlsx`);
            message.success('Xuất file Excel thành công!');
        } catch (error) {
            message.error('Có lỗi xảy ra khi xuất file Excel: ' + error.message);
        } finally {
            setExporting(false);
        }
    };

    return (
        <AdminLayout title="Thí sinh có điểm IELTS">
            <Space style={{ marginBottom: 16 }}>
                <Card size="small">
                    <Statistic
                        title="Tổng số thí sinh"
                        value={data.length}
                        prefix={<StarOutlined style={{ color: '#1890ff' }} />}
                        loading={loading}
                    />
                </Card>
                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={exportToExcel}
                    loading={exporting}
                    disabled={loading || data.length === 0}
                >
                    Xuất Excel
                </Button>
            </Space>
            <Table
                columns={buildColumns(navigate)}
                dataSource={data}
                rowKey="student_code"
                loading={loading}
                size="small"
                scroll={{ x: 700 }}
                pagination={{
                    pageSize: 20,
                    showTotal: (t) => `Tổng ${t} thí sinh`,
                    showSizeChanger: true,
                }}
            />
        </AdminLayout>
    );
};

export default AdminIeltsList;
